/**
 * History Helper Utilities
 * Utility functions for history-related operations
 */

import { format } from 'date-fns';
import { SearchEntry } from '@/store/chatHistoryStore';

/**
 * Formats a date for display in history list
 * @param entry - The search entry containing date information
 * @returns Formatted date string (e.g., "Dec 10")
 */
export const formatHistoryDate = (entry: SearchEntry): string => {
  // Try to get timestamp from entry if available (prioritize timestamp over aiReply)
  const entryWithTimestamp = entry as SearchEntry & {
    timestamp?: number;
    last_update_time?: number;
    lastUpdateTime?: number;
  };

  let date: Date | null = null;

  // First, try to get date from timestamp fields
  if (entryWithTimestamp.lastUpdateTime) {
    // lastUpdateTime is in milliseconds (from API response)
    date = new Date(entryWithTimestamp.lastUpdateTime);
  } else if (entryWithTimestamp.last_update_time) {
    // last_update_time is in milliseconds (from ConversationItem)
    // Check if it's already in milliseconds (> 1e12) or seconds (< 1e12)
    const timestamp = entryWithTimestamp.last_update_time;
    date = new Date(timestamp > 1e12 ? timestamp : timestamp * 1000);
  } else if (entryWithTimestamp.timestamp) {
    // timestamp might be in seconds, convert to milliseconds
    date = new Date(entryWithTimestamp.timestamp * 1000);
  }

  // If we have a date from timestamp, format it
  if (date && !isNaN(date.getTime())) {
    return format(date, 'MMM d');
  }

  // Try to use the formatted date from aiReply (MMM d, yyyy format)
  // For API conversations, aiReply contains the formatted date
  if (entry.aiReply) {
    try {
      // Check if aiReply is a date string in format "MMM d, yyyy"
      const dateMatch = entry.aiReply.match(
        /^([A-Za-z]{3}\s+\d{1,2}),\s+\d{4}$/
      );
      if (dateMatch) {
        // Extract just "MMM d" part
        return dateMatch[1];
      }
      // If it's already in a short format (MMM d), use it directly
      const shortDateMatch = entry.aiReply.match(/^[A-Za-z]{3}\s+\d{1,2}$/);
      if (shortDateMatch) {
        return entry.aiReply;
      }
    } catch {
      // Fall through to footer parsing
    }
  }

  // Fallback to footer date (yyyy-MM-dd format)
  if (entry.footer) {
    try {
      const parsedDate = new Date(entry.footer);
      if (!isNaN(parsedDate.getTime())) {
        return format(parsedDate, 'MMM d');
      }
    } catch {
      // Return empty if parsing fails
    }
  }

  return '';
};

/**
 * Formats time for display in history list
 * @param entry - The search entry containing date information
 * @returns Formatted time string (e.g., "10:30 AM")
 */
export const formatHistoryTime = (entry: SearchEntry): string => {
  // Try to get timestamp from entry if available
  let date: Date | null = null;

  // Check if entry has a timestamp property (from API)
  const entryWithTimestamp = entry as SearchEntry & {
    timestamp?: number;
    last_update_time?: number;
    lastUpdateTime?: number;
  };

  if (entryWithTimestamp.lastUpdateTime) {
    // lastUpdateTime is in milliseconds (from API response)
    date = new Date(entryWithTimestamp.lastUpdateTime);
  } else if (entryWithTimestamp.last_update_time) {
    // last_update_time is in milliseconds (from ConversationItem)
    // Check if it's already in milliseconds (> 1e12) or seconds (< 1e12)
    const timestamp = entryWithTimestamp.last_update_time;
    date = new Date(timestamp > 1e12 ? timestamp : timestamp * 1000);
  } else if (entryWithTimestamp.timestamp) {
    // timestamp might be in seconds, convert to milliseconds
    date = new Date(entryWithTimestamp.timestamp * 1000);
  } else if (entry.footer) {
    // Try to parse from footer date
    try {
      const parsedDate = new Date(entry.footer);
      if (!isNaN(parsedDate.getTime())) {
        date = parsedDate;
      }
    } catch {
      // Ignore parsing errors
    }
  }

  if (date && !isNaN(date.getTime())) {
    return format(date, 'h:mm a');
  }

  return '';
};

/**
 * Extracts the title from a chat history entry
 * @param entry - The search entry to extract title from
 * @returns Title text
 */
export const getChatTitle = (entry: SearchEntry): string => {
  if (entry.title) {
    return entry.title;
  }
  if (entry.input) {
    return entry.input;
  }
  return 'Untitled chat';
};

/**
 * Extracts the description from a chat history entry
 * @param entry - The search entry to extract description from
 * @param maxLength - Maximum length of the description (default: 150)
 * @returns Description text
 */
export const getChatDescription = (
  entry: SearchEntry,
  maxLength: number = 150
): string | undefined => {
  // If there's a title, use aiReply as description
  if (entry.title && entry.aiReply) {
    const description = entry.aiReply.trim();
    if (description.length > maxLength) {
      return `${description.substring(0, maxLength)}...`;
    }
    return description;
  }

  // If no title but there's input and aiReply, use aiReply as description
  if (entry.input && entry.aiReply) {
    const description = entry.aiReply.trim();
    if (description.length > maxLength) {
      return `${description.substring(0, maxLength)}...`;
    }
    return description;
  }

  return undefined;
};

/**
 * Extracts a summary text from a chat history entry
 * @param entry - The search entry to extract summary from
 * @param maxLength - Maximum length of the summary (default: 100)
 * @returns Summary text
 * @deprecated Use getChatTitle and getChatDescription instead
 */
export const getChatSummary = (
  entry: SearchEntry,
  maxLength: number = 100
): string => {
  if (entry.title) {
    return entry.title.length > maxLength
      ? `${entry.title.substring(0, maxLength)}...`
      : entry.title;
  }
  if (entry.input) {
    return entry.input.length > maxLength
      ? `${entry.input.substring(0, maxLength)}...`
      : entry.input;
  }
  if (entry.aiReply) {
    return entry.aiReply.length > maxLength
      ? `${entry.aiReply.substring(0, maxLength)}...`
      : entry.aiReply;
  }
  return 'Untitled chat';
};

/**
 * Filters history entries based on search query
 * @param entries - Array of search entries
 * @param query - Search query string
 * @returns Filtered array of entries
 */
export const filterHistoryEntries = (
  entries: SearchEntry[],
  query: string
): SearchEntry[] => {
  if (!query.trim()) {
    return entries;
  }

  const searchQuery = query.toLowerCase();
  return entries.filter(entry => {
    const searchText =
      `${entry.input} ${entry.title || ''} ${entry.aiReply || ''}`.toLowerCase();
    return searchText.includes(searchQuery);
  });
};

/**
 * Reverses the order of history entries to show newest first
 * @param entries - Array of search entries
 * @returns Reversed array
 */
export const reverseHistoryOrder = (entries: SearchEntry[]): SearchEntry[] => {
  return [...entries].reverse();
};
